"use client";

import { Icon, ospThemes } from "@ospweb/sdk-ui";
import React, { useState } from "react";

import { AppImage } from "@/src/components/NextImage";
import { cn } from "utils";
import { I18nUtil } from "@utils/i18n";
import Translations from "@translate/components/Portfolio/PortfolioUpgradePrompt/PortfolioUpgradePrompt";
import { useIsPc } from "@/src/hooks";

export interface PortfolioUpgradePromptProps {
  /** 自定义类名 */
  className?: string;
  /** 点击导入按钮的回调 */
  onImportClick?: () => void;
  /** 是否是会员（PRO 或 Expert） */
  isMember?: boolean;
}

/** 功能卡片配置类型 */
interface FeatureCardConfig {
  /** 图标名称 */
  iconName: string;
  /** 卡片头部标题 */
  headerTitle: string;
  /** 内容卡片标题 */
  contentTitle: string;
  /** 内容卡片描述 */
  contentDescription: string;
  /** 背景图片路径（桌面端） */
  backgroundImage: string;
  /** 背景图片路径（移动端 h5） */
  backgroundImageH5: string;
  /** 背景图片 alt 文本 */
  backgroundImageAlt: string;
  /** 是否使用 backdrop-blur 效果 */
  hasBackdropBlur?: boolean;
  /** 对齐方式 */
  align?: "center" | "end";
  /** 内容区域最大宽度类名 */
  contentMaxWidthClass?: string;
  /** 是否需要额外的外层包裹容器 */
  hasExtraWrapper?: boolean;
}

/** 功能卡片组件属性 */
interface FeatureCardProps {
  config: FeatureCardConfig;
  onClick?: () => void;
}

/**
 * 功能卡片子组件
 * 用于渲染单个功能卡片
 */
const FeatureCard: React.FC<FeatureCardProps> = ({ config, onClick }) => {
  const {
    iconName,
    headerTitle,
    contentTitle,
    contentDescription,
    backgroundImage,
    backgroundImageH5,
    backgroundImageAlt,
    hasBackdropBlur = false,
    align = "center",
    contentMaxWidthClass = "max-w-[50%]",
    hasExtraWrapper = false,
  } = config;

  const outerContainerClass = cn(
    "flex flex-1 items-center min-w-0",
    align === "center" ? "justify-center" : "justify-end",
  );

  const contentCardClass = cn(
    "bg-background_solid_tertiary flex flex-col gap-6 h-[140px] md:h-[176px] items-start overflow-hidden py-3 px-4 md:px-6 md:py-6 rounded-xl w-full relative md:cursor-pointer",
    hasBackdropBlur && "backdrop-blur-2xl",
  );

  const [isPc] = useIsPc();

  const cardContent = (
    <div
      data-openreplay-label="PortfolioUpgradePrompt · PortfolioUpgradePrompt · div"
      className="flex flex-col gap-5 items-start w-full"
      onClick={onClick}
    >
      {/* 卡片头部 */}
      <div className="flex gap-3 items-center flex-row w-full">
        {/* 图标 */}
        <div className="flex items-center justify-center rounded-lg shrink-0">
          <Icon
            name={iconName}
            size={isPc ? 32 : 20}
            color={ospThemes.colors.content_brand_primary}
          />
        </div>
        {/* 标题和描述 */}
        <div className="flex flex-1 flex-col items-start min-w-0">
          <div className="flex flex-col items-start w-full">
            <h2
              aria-label="Portfolio Upgrade Prompt h2"
              data-openreplay-label="Portfolio Upgrade Prompt h2 Semantic"
              id="sem-portfolio-upgrade-prompt-h2"
              className="headline_regular_s text-content_primary tracking-[-0.6px]"
            >
              {headerTitle}
            </h2>
          </div>
        </div>
      </div>
      {/* 卡片内容 */}
      <div className={contentCardClass}>
        <div
          className={cn(
            contentMaxWidthClass,
            "md:w-full flex flex-1 flex-col md:justify-start justify-center relative z-10",
          )}
        >
          <h3
            aria-label="Portfolio Upgrade Prompt h3"
            data-openreplay-label="Portfolio Upgrade Prompt h3 Semantic"
            id="sem-portfolio-upgrade-prompt-h3"
            className="title_l text-content_primary"
          >
            {contentTitle}
          </h3>
          <p className="body_s text-content_secondary w-full whitespace-pre-wrap break-words">
            {contentDescription}
          </p>
        </div>
        {/* 背景图片 */}
        <div className="absolute inset-0 border-1 border-border_white_transparent5 rounded-xl overflow-hidden">
          {/* 移动端 h5 图片 */}
          <AppImage
            src={backgroundImageH5}
            alt={backgroundImageAlt}
            width={640}
            height={360}
            className="absolute inset-0 h-full w-full object-cover md:hidden"
          />
          {/* 桌面端图片 */}
          <AppImage
            src={backgroundImage}
            alt={backgroundImageAlt}
            width={640}
            height={360}
            className="absolute inset-0 hidden h-full w-full object-cover md:block"
          />
        </div>
      </div>
    </div>
  );

  if (hasExtraWrapper) {
    return (
      <div className={outerContainerClass}>
        <div className="flex flex-1 items-center justify-center min-w-0">
          {cardContent}
        </div>
      </div>
    );
  }

  return <div className={outerContainerClass}>{cardContent}</div>;
};

const getFeatureCardsConfig = (
  I18n: ReturnType<typeof I18nUtil.createScoped>,
): FeatureCardConfig[] => [
  {
    iconName: "import portfolio",
    headerTitle: I18n.t("Sync Your Accounts"),
    contentTitle: I18n.t("All Your Accounts, All in One Place"),
    contentDescription: I18n.t(
      "Connect your favorite platforms effortlessly and stop switching between different apps.",
    ),
    backgroundImage: "/assets/images/portfolio/link_your_broker_v3.webp",
    backgroundImageH5: "/assets/images/portfolio/link_your_broker_v3.webp",
    backgroundImageAlt: "Link Your Broker",
    hasBackdropBlur: false,
    align: "center",
    contentMaxWidthClass: "max-w-[50%] md:max-w-[60%]",
    hasExtraWrapper: false,
  },
  {
    iconName: "plus-chart",
    headerTitle: I18n.t("Get Asset Analysis"),
    contentTitle: I18n.t("Smart Insights at a Glance"),
    contentDescription: I18n.t(
      "See your wealth grow with intuitive charts and get a personalized summary of your portfolio every day.",
    ),
    backgroundImage: "/assets/images/portfolio/chart_analysis_v3.webp",
    backgroundImageH5: "/assets/images/portfolio/chart_analysis_v3.webp",
    backgroundImageAlt: "Chart Analysis",
    hasBackdropBlur: true,
    align: "end",
    contentMaxWidthClass: "max-w-[50%] md:max-w-[60%]",
    hasExtraWrapper: true,
  },
];

/**
 * 会员升级提示组件
 * 当用户没有会员或会员过期时显示，提示用户升级 PRO 并导入投资组合
 */
export const PortfolioUpgradePrompt: React.FC<PortfolioUpgradePromptProps> = ({
  className,
  onImportClick,
  isMember = false,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const locale = I18nUtil.getCurrentLocale();
  const I18n = I18nUtil.createScoped(Translations, locale);
  // 处理导入按钮点击，直接调用传入的回调
  // 会员检查和升级弹窗逻辑已移至 useHandleImportClick Hook
  const handleImportClick = () => {
    onImportClick?.();
  };

  const featureCards = getFeatureCardsConfig(I18n);

  // 根据会员状态显示不同的按钮文本
  const buttonText = isMember
    ? I18n.t("Import Portfolio Now")
    : I18n.t("Become PRO");

  return (
    <div
      className={cn(
        "flex flex-col md:gap-12 gap-8 items-start justify-center w-full",
        className,
      )}
      style={{
        paddingBottom: "calc(56px + var(--mobile-tab-safe-height, 0px))",
      }}
    >
      {/* 头部区域 */}
      <div className="flex flex-col gap-4 items-start w-full">
        <div className="flex flex-col gap-2 items-start w-full">
          {/* 标题 */}
          <div className="flex items-center w-full">
            <h1
              aria-label="Portfolio Upgrade Prompt h1"
              data-openreplay-label="Portfolio Upgrade Prompt h1 Semantic"
              id="sem-portfolio-upgrade-prompt-h1"
              className="headline_regular_m md:headline_regular_l text-content_primary tracking-[-1px]"
            >
              {I18n.t("Become")}
              <span className="text-content_brand_primary mx-2">
                {I18n.t("PRO")}
              </span>
              {I18n.t("and Unlock Portfolio Syncing")}
            </h1>
          </div>
          {/* 描述 */}
          <p className="md:block hidden body_l text-content_secondary w-full whitespace-pre-wrap">
            {I18n.t(
              "Add your holdings so Edggy can start building around you. Edggy will learn to think like you, and get smarter to keep you unstoppable.",
            )}
          </p>
        </div>
        {/* 导入按钮 - 桌面端 */}
        <button
          aria-label="Portfolio Upgrade Prompt"
          data-openreplay-label="Portfolio Upgrade Prompt Button"
          id="btn-portfolio-upgrade-prompt"
          onClick={handleImportClick}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          className={cn(
            "bg-background_accent4_overlay100",
            "flex gap-0.5 items-center justify-center min-w-[80px] px-4 py-3 rounded-lg",
            "transition-colors",
            "cursor-pointer",
            isHovered && "active",
            isHovered && "before:!p-[1px]",
            "hidden md:flex",
          )}
        >
          <span className="button_m font-bold text-content_inverse_primary">
            {buttonText}
          </span>
          <Icon
            name="right-arrow-alt"
            size={20}
            color={ospThemes.colors.content_inverse_primary}
          />
        </button>
      </div>
      {/* 功能卡片区域 */}
      <div className="flex gap-12 w-full max-md:flex-col">
        {featureCards.map((config, index) => (
          <FeatureCard
            data-openreplay-label="PortfolioUpgradePrompt · PortfolioUpgradePrompt · FeatureCard"
            key={index}
            config={config}
            onClick={handleImportClick}
          />
        ))}
      </div>
      {/* 导入按钮 - 移动端吸底 */}
      <div
        style={{
          bottom: "calc(var(--mobile-tab-input-safe-height, 0px))",
        }}
        className={cn(
          "fixed left-0 right-0 z-[810]",
          "md:hidden",
          "bg-background_solid_secondary",
          "px-4 mb-4",
        )}
      >
        <button
          aria-label="Portfolio Upgrade Prompt"
          data-openreplay-label="Portfolio Upgrade Prompt Button"
          id="btn-portfolio-upgrade-prompt-2"
          onClick={handleImportClick}
          className={cn(
            "bg-background_solid_secondary",
            "flex gap-1 items-center justify-center w-full px-4 py-3 rounded-lg",
            "transition-colors",
            "cursor-pointer border border-border_brand_solid",
          )}
        >
          <Icon
            name="pricing"
            size={20}
            color={ospThemes.colors.content_brand_primary}
          />
          <span className="text-gradient button_m font-bold text-transparent">
            {buttonText}
          </span>
        </button>
      </div>
    </div>
  );
};
